import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Clock, Plus, Trash2, Send, Receipt, DollarSign, Filter, X, Pencil, Unlock, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["development", "design", "meeting", "project_management", "support", "testing", "consulting", "other"];
const CAT_COLORS = { development: "bg-blue-500/10 text-blue-400", design: "bg-purple-500/10 text-purple-400", meeting: "bg-yellow-500/10 text-yellow-400", project_management: "bg-[#F59073]/10 text-[#F59073]", support: "bg-green-500/10 text-green-400", testing: "bg-orange-500/10 text-orange-400", consulting: "bg-pink-500/10 text-pink-400", other: "bg-[#1E3050]/40 text-[#857973]" };

export default function TimeTracking({ projectId, projectTitle, customerId }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterCat, setFilterCat] = useState("");
  const blankForm = { date: new Date().toISOString().slice(0, 10), hours: "", description: "", category: "development", hourly_rate: "", currency: "EUR", entry_type: "time", expense_amount: "" };
  const [form, setForm] = useState(blankForm);

  const fetchData = useCallback(async () => {
    const [entriesRes, summaryRes] = await Promise.all([
      axios.get(`${API}/admin/time-entries`, { params: { project_id: projectId }, withCredentials: true }),
      axios.get(`${API}/admin/projects/${projectId}/time-summary`, { withCredentials: true }),
    ]);
    setEntries(entriesRes.data);
    setSummary(summaryRes.data);
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveEntry = async (e) => {
    e.preventDefault();
    const payload = {
      project_id: projectId, date: form.date, hours: Number(form.hours) || 0,
      description: form.description, category: form.category,
      hourly_rate: Number(form.hourly_rate) || 0, currency: form.currency,
      entry_type: form.entry_type, expense_amount: form.entry_type === "expense" ? Number(form.expense_amount) || 0 : null,
    };
    if (editingId) {
      await axios.put(`${API}/admin/time-entries/${editingId}`, payload, { withCredentials: true });
    } else {
      await axios.post(`${API}/admin/time-entries`, payload, { withCredentials: true });
    }
    closeModal();
    fetchData();
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(blankForm);
    setShowAdd(true);
  };

  const openEdit = (entry) => {
    setEditingId(entry.id);
    setForm({
      date: entry.date || new Date().toISOString().slice(0, 10),
      hours: entry.hours ?? "",
      description: entry.description || "",
      category: entry.category || "development",
      hourly_rate: entry.hourly_rate ?? "",
      currency: entry.currency || "EUR",
      entry_type: entry.entry_type || "time",
      expense_amount: entry.expense_amount ?? "",
    });
    setShowAdd(true);
  };

  const closeModal = () => {
    setShowAdd(false);
    setEditingId(null);
    setForm(blankForm);
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await axios.delete(`${API}/admin/time-entries/${id}`, { withCredentials: true });
    fetchData();
  };

  const unlockEntry = async (id) => {
    if (!window.confirm("Unlock this entry? It will be detached from its invoice and can be edited again.")) return;
    await axios.post(`${API}/admin/time-entries/${id}/unlock`, {}, { withCredentials: true });
    fetchData();
  };

  const markInvoiced = async (id) => {
    if (!window.confirm("Mark this entry as invoiced manually? It won't be picked up next time you create an invoice from time.")) return;
    await axios.post(`${API}/admin/time-entries/${id}/mark-invoiced`, {}, { withCredentials: true });
    fetchData();
  };

  const sendReport = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/projects/${projectId}/send-time-report`, { origin_url: window.location.origin }, { withCredentials: true });
      alert(`Time report sent to ${data.to}`);
    } catch (err) { alert(err.response?.data?.detail || "Failed to send"); }
  };

  const generateInvoice = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/projects/${projectId}/invoice-from-time`, { origin_url: window.location.origin }, { withCredentials: true });
      alert(`Invoice created: ${data.total} ${data.currency} (${data.items_count} items)`);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Failed to create invoice"); }
  };

  const shareReport = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/projects/${projectId}/time-report`, {}, { withCredentials: true });
      const url = `${window.location.origin}/time-report/${data.token}`;
      navigator.clipboard.writeText(url);
      alert(`Report link copied: ${url}`);
    } catch (err) { alert("Failed to generate link"); }
  };

  const filtered = filterCat ? entries.filter(e => e.category === filterCat) : entries;
  const uninvoiced = entries.filter(e => !e.invoiced && !e.invoice_id).length;

  return (
    <div data-testid="time-tracking">
      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
            <span className="font-mono text-[10px] text-[#857973]">HOURS</span>
            <div className="font-outfit font-bold text-xl text-white mt-1">{summary.total_hours}h</div>
          </div>
          <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
            <span className="font-mono text-[10px] text-[#857973]">TIME COST</span>
            <div className="font-outfit font-bold text-xl text-[#F59073] mt-1">{summary.total_cost.toLocaleString()} {summary.currency}</div>
          </div>
          <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
            <span className="font-mono text-[10px] text-[#857973]">EXPENSES</span>
            <div className="font-outfit font-bold text-xl text-white mt-1">{summary.total_expenses.toLocaleString()} {summary.currency}</div>
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={openAdd} data-testid="add-time-entry-btn" className="flex items-center gap-1.5 bg-[#F59073] text-black px-3 py-1.5 rounded-sm text-xs font-medium">
          <Plus className="w-3.5 h-3.5" /> Log time / expense
        </button>
        <button onClick={sendReport} data-testid="send-report-btn" className="flex items-center gap-1.5 bg-[#1E3050]/40 text-[#857973] border border-[#1E3050] px-3 py-1.5 rounded-sm text-xs hover:text-white transition-colors">
          <Send className="w-3.5 h-3.5" /> Send report
        </button>
        <button onClick={shareReport} className="flex items-center gap-1.5 bg-[#1E3050]/40 text-[#857973] border border-[#1E3050] px-3 py-1.5 rounded-sm text-xs hover:text-white transition-colors">
          <Clock className="w-3.5 h-3.5" /> Share link
        </button>
        {uninvoiced > 0 && (
          <button onClick={generateInvoice} data-testid="invoice-from-time-btn" className="flex items-center gap-1.5 bg-green-500/10 text-green-400 border border-green-500/30 px-3 py-1.5 rounded-sm text-xs hover:bg-green-500/20 transition-colors">
            <Receipt className="w-3.5 h-3.5" /> Invoice {uninvoiced} entries
          </button>
        )}
      </div>

      {/* Category filter */}
      <div className="flex gap-1 flex-wrap mb-4">
        <button onClick={() => setFilterCat("")} className={`px-2 py-1 rounded-sm text-[10px] font-mono transition-colors ${!filterCat ? "bg-[#F59073]/10 text-[#F59073]" : "text-[#857973] hover:text-white"}`}>all</button>
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => setFilterCat(c)} className={`px-2 py-1 rounded-sm text-[10px] font-mono transition-colors ${filterCat === c ? "bg-[#F59073]/10 text-[#F59073]" : "text-[#857973] hover:text-white"}`}>{c.replace("_", " ")}</button>
        ))}
      </div>

      {/* Entries list */}
      <div className="space-y-1.5">
        {filtered.map(e => (
          <div key={e.id} data-testid={`entry-${e.id}`} className={`flex items-center justify-between bg-[#0F1F3A] border rounded-sm px-4 py-3 ${e.invoiced ? "border-green-500/10 opacity-60" : "border-[#1E3050]"}`}>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className="text-xs text-[#857973] font-mono w-20 flex-shrink-0">{e.date}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-mono flex-shrink-0 ${CAT_COLORS[e.category] || CAT_COLORS.other}`}>{e.category?.replace("_", " ")}</span>
              <span className="text-sm text-white truncate">{e.description}</span>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              {e.entry_type === "time" ? (
                <>
                  <span className="text-sm text-[#857973] font-mono">{e.hours}h</span>
                  <span className="text-sm text-white font-mono">{(e.hours * e.hourly_rate).toLocaleString()} {e.currency}</span>
                </>
              ) : (
                <>
                  <DollarSign className="w-3.5 h-3.5 text-yellow-400" />
                  <span className="text-sm text-white font-mono">{e.expense_amount?.toLocaleString()} {e.currency}</span>
                </>
              )}
              {e.invoiced && (
                <>
                  <span className={`text-[10px] font-mono ${e.manually_invoiced ? "text-[#D9A872]" : "text-green-400"}`} title={e.manually_invoiced ? "Manually marked as invoiced" : "Invoiced & sent — locked"}>
                    {e.manually_invoiced ? "MANUAL" : "INV"}
                  </span>
                  <button onClick={() => unlockEntry(e.id)} data-testid={`unlock-entry-${e.id}`} className="text-[#857973] hover:text-[#D9A872] transition-colors" title="Unlock entry (mark as not invoiced)">
                    <Unlock className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
              {!e.invoiced && e.invoice_id && <span className="text-[10px] text-[#D9A872] font-mono" title="On a draft invoice">DRAFT INV</span>}
              {!e.invoiced && !e.invoice_id && (
                <button onClick={() => markInvoiced(e.id)} data-testid={`mark-invoiced-${e.id}`} className="text-[#857973] hover:text-green-400 transition-colors" title="Mark as invoiced manually (skip invoice creation)">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </button>
              )}
              {!e.invoiced && (
                <>
                  <button onClick={() => openEdit(e)} data-testid={`edit-entry-${e.id}`} className="text-[#857973] hover:text-[#F59073] transition-colors" title="Edit">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => deleteEntry(e.id)} className="text-[#857973] hover:text-red-400 transition-colors" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-center text-[#857973] text-sm py-8">No entries logged yet</p>}
      </div>

      {/* Add / edit entry modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={closeModal} />
          <div className="relative bg-[#0F1F3A] border border-[#1E3050] rounded-sm w-full max-w-2xl p-6 z-10 max-h-[90vh] overflow-y-auto" data-testid="add-entry-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-outfit font-medium text-white">{editingId ? "Edit entry" : "Log time or expense"}</h3>
              <button onClick={closeModal} className="text-[#857973] hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            {/* Type toggle */}
            <div className="flex gap-2 mb-4">
              <button onClick={() => setForm(p => ({ ...p, entry_type: "time" }))} className={`flex-1 py-2 rounded-sm text-sm font-medium transition-colors ${form.entry_type === "time" ? "bg-[#F59073]/10 text-[#F59073] border border-[#F59073]/30" : "bg-[#1E3050]/40 text-[#857973] border border-[#1E3050]"}`}>
                <Clock className="w-4 h-4 inline mr-1.5" />Time
              </button>
              <button onClick={() => setForm(p => ({ ...p, entry_type: "expense" }))} className={`flex-1 py-2 rounded-sm text-sm font-medium transition-colors ${form.entry_type === "expense" ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/30" : "bg-[#1E3050]/40 text-[#857973] border border-[#1E3050]"}`}>
                <DollarSign className="w-4 h-4 inline mr-1.5" />Expense
              </button>
            </div>

            <form onSubmit={saveEntry} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">DATE</label>
                  <input type="date" required value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">CATEGORY</label>
                  <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
                  </select>
                </div>
              </div>

              {form.entry_type === "time" ? (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="font-mono text-[10px] text-[#857973] mb-1 block">HOURS</label>
                    <input type="number" step="0.25" required value={form.hours} onChange={e => setForm(p => ({ ...p, hours: e.target.value }))} placeholder="0" className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="entry-hours" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] text-[#857973] mb-1 block">RATE/H</label>
                    <input type="number" step="0.01" required value={form.hourly_rate} onChange={e => setForm(p => ({ ...p, hourly_rate: e.target.value }))} placeholder="0" className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="entry-rate" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] text-[#857973] mb-1 block">CURRENCY</label>
                    <select value={form.currency} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                      <option value="EUR">EUR</option><option value="SEK">SEK</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-mono text-[10px] text-[#857973] mb-1 block">AMOUNT</label>
                    <input type="number" step="0.01" required value={form.expense_amount} onChange={e => setForm(p => ({ ...p, expense_amount: e.target.value }))} placeholder="0" className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] text-[#857973] mb-1 block">CURRENCY</label>
                    <select value={form.currency} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                      <option value="EUR">EUR</option><option value="SEK">SEK</option>
                    </select>
                  </div>
                </div>
              )}

              <div>
                <label className="font-mono text-[10px] text-[#857973] mb-1 block">DESCRIPTION</label>
                <textarea
                  required
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="What did you work on? Add as much detail as you need — decisions, links, blockers, next steps."
                  rows={7}
                  className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2.5 rounded-sm text-sm focus:border-[#D9A872] outline-none resize-y leading-relaxed font-manrope"
                  data-testid="entry-description"
                />
              </div>

              {form.entry_type === "time" && form.hours && form.hourly_rate && (
                <div className="text-right text-sm text-[#857973]">
                  Total: <span className="text-white font-mono">{(Number(form.hours) * Number(form.hourly_rate)).toLocaleString()} {form.currency}</span>
                </div>
              )}

              <button type="submit" data-testid="submit-entry-btn" className="w-full bg-[#F59073] text-black py-2.5 rounded-sm text-sm font-medium">
                {editingId ? "Save changes" : (form.entry_type === "time" ? "Log hours" : "Add expense")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
